import React from 'react';

import {
  constants as extrasConstants,
  methods as extrasMethods,
} from 'utils/extras';
import { joinElementsWithSeparatorAndEndingSeparator } from 'utils/array';

import PriceConceptsBreakdown from 'components/PriceConceptsBreakdown';
import buildUrgentMessage from './buildUrgentMessage';
import { constants as orderConstants } from 'utils/order';
import { formatters as dateAndTimeFormatters } from 'utils/dateAndTimes';
import { CANCELLABLE_BY_SHIPPER_STATUSES } from 'utils/order/constants';

const getCommonExtras = ({ priceInfo, t }) => {
  const extrasToShow = priceInfo.extra_prices.filter(
    (extra) =>
      !extrasConstants.RECURRING_ORDER_NOT_COMMON_EXTRAS.includes(extra.concept)
  );

  return extrasToShow
    .sort(extrasMethods.sortExtrasByPriority())
    .map((extra) => ({
      name: t(`extraNames:${extra.concept}`),
      value: Number(extra.net),
      ...extra,
    }));
};

const getTotalPrices = ({ quotes }) =>
  quotes.map((q) => ({
    title: q.totalPriceTitle,
    netPrice: Number(q.net_full_price),
    fullPrice: Number(q.gross_full_price),
  }));

const getExtras = ({ priceInfo, filterExtras, t, removeZeros }) => {
  const extras =
    (priceInfo && [
      ...(priceInfo.base_extra_prices || []),
      ...priceInfo.extra_prices,
    ]) ||
    [];
  const extrasWithoutZeros = removeZeros
    ? extras.filter((extraItem) => Number(extraItem.net) !== 0)
    : extras;
  const extrasToShow = filterExtras
    ? extrasWithoutZeros.filter(
        (extraItem) =>
          Number(extraItem.net) > 0 ||
          extraItem.concept === 'DIRECT_FLEX_DISCOUNT'
      )
    : extrasWithoutZeros;
  return extrasToShow
    .sort(extrasMethods.sortExtrasByPriority())
    .map((extra) => {
      return {
        name: t(`extraNames:${extra.concept}`),
        value: Number(extra.net),
        ...extra,
      };
    });
};

export const getBaseConcept = (quote, t) => {
  if (quote?.is_contract) {
    return {
      tagLabel: t('negotiated_price'),
    };
  }

  if (!quote?.can_xdock) return {};

  const priority = quote?.ontruck_24_priority;
  const labels = {
    tag: {
      HIGH: t('can_xdock'),
      LOW: t('can_xdock_low_prio'),
    },
    tooltip: {
      HIGH: t('can_xdock_info'),
      LOW: t('can_xdock_info_low_prio'),
    },
    className: {
      HIGH: 'xdock',
      LOW: 'xdock-low-prio',
    },
  };

  return {
    concept: t('base_price'),
    tagLabel: labels.tag[priority] || labels.tag.LOW,
    tagLabelInfoTooltipMessage: labels.tooltip[priority] || labels.tooltip.LOW,
    tagLabelClassName: labels.className[priority] || labels.className.Low,
  };
};

const getNullableExtras = (t, quote = {}) => {
  if (!quote?.price_breakdown?.extra_discounts) return [];
  return quote.price_breakdown.extra_discounts.map((extraDiscount) => {
    return buildDiscountConcept(t, {
      concept: extraDiscount.concept,
      net: extraDiscount.amount_discount,
      amount: extraDiscount.amount,
    });
  });
};

const getDiscounts = (t, quote = {}) => {
  if (
    !quote?.price_breakdown ||
    quote.price_breakdown.discount_applied <= 0 ||
    quote.price_breakdown.percentage_discount_applied <= 0
  )
    return [];
  return [
    {
      concept: `${t('discount')}`,
      tagLabel: `${quote?.price_breakdown.percentage_discount_applied}%`,
      tagLabelClassName: 'green',
      tagLabelInfoTooltipMessage: '',
      tooltipInnerComponent: () => null,
      conceptQuantity: -Math.abs(quote?.price_breakdown.discount_applied),
    },
  ];
};

const getTranslatedOrEmpty = (t, key) => (t(key) !== key ? t(key) : '');

const getPriceConcepts = ({ extras, t, quote = {}, basePrice }) => {
  return [
    {
      concept: `${t('base_price')} ${
        quote?.vehicle_type
          ? [
              getTranslatedOrEmpty(
                t,
                `vehicles_connectors.${quote.vehicle_type}`
              ),
              getTranslatedOrEmpty(t, `vehicles.${quote.vehicle_type}`),
              getTranslatedOrEmpty(
                t,
                `vehicles_or_greater.${quote.vehicle_type}`
              ),
            ]
              .filter(Boolean)
              .join(' ')
          : ''
      }`,
      tagLabel: false,
      tagLabelClassName: '',
      tagLabelInfoTooltipMessage: '',
      tooltipInnerComponent: () => null,
      conceptQuantity: basePrice,
    },
    ...getDiscounts(t, quote),
    ...getNullableExtras(t, quote),
    ...extras.map((extra) => buildExtraConcept({ t, extra })),
  ];
};

function buildExtraConcept({
  t,
  extra: { name, concept, value, simple_message_token = null },
}) {
  const tooltipInnerComponent =
    orderConstants.URGENCY_TYPES.includes(concept) && !!simple_message_token
      ? () => buildUrgentMessage({ t, token: simple_message_token })
      : () => t(`extras.${concept}.tooltip`);

  const extraConcept = {
    concept: `${name}`,
    tooltipInnerComponent: tooltipInnerComponent,
    conceptQuantity: value,
  };

  if (extraConcept.concept === 'DIRECT_FLEX_DISCOUNT') {
    extraConcept.concept = t(`extras.ONTRUCK_ECONOMY_DISCOUNT.concept`);
    extraConcept.tooltipInnerComponent = () => null;
  }

  if (extraConcept.concept === 'INCREMENT_BY_SHORT_WINDOW_FLEX') {
    extraConcept.concept = t(
      `extras.ONTRUCK_ECONOMY_INCREMENT_FOR_SHORT_WINDOW.concept`
    );
    extraConcept.tooltipInnerComponent = () => null;
  }

  return extraConcept;
}

const buildDiscountConcept = (t, { concept, net, amount }) => {
  const isExtraDiscount = extrasConstants.DISCOUNT_EXTRAS.includes(concept);
  return {
    concept: t(`extraNames:${concept}`),
    tooltipInnerComponent: () => t(`extras.${concept}.tooltip`),
    conceptQuantity: Number(net),
    strikethrough: !isExtraDiscount,
    ...(isExtraDiscount && amount
      ? {
          tagLabel: `${amount}%`,
          tagLabelClassName: 'green',
        }
      : {}),
  };
};

const getGroupedQuotes = ({ quote, t }) => {
  const pricingDays = Object.entries(quote.pricing_days).filter((q) => !!q[1]);
  return pricingDays.reduce((acc, q) => {
    const dayTranslated = t(`Recurring:weekdays_plural.${q[0]}`);
    const quoteForDay = q[1];
    const netPrice = quoteForDay.net_full_price;
    const index = acc.findIndex(
      (el) => el && el[1].net_full_price === netPrice
    );

    if (index >= 0) {
      acc[index][0].push(dayTranslated);
    } else {
      acc.push([[dayTranslated], quoteForDay]);
    }
    return acc;
  }, []);
};

const getRecurringPriceConcepts = ({ quote, t }) => {
  const groupedQuotes = getGroupedQuotes({ quote, t });

  const daysQuotes = groupedQuotes.map((q) => {
    const title = joinElementsWithSeparatorAndEndingSeparator(
      q[0],
      ', ',
      t('Recurring:and')
    );
    return [title, { totalPriceTitle: title, ...q[1] }];
  });

  // daysQuotes have this structure: [['day_name', {..quote_for_that_day}], ...]
  return daysQuotes.map((q) => {
    const extras = getExtras({ priceInfo: q[1], filterExtras: true, t });
    const dayConcepts = getPriceConcepts({
      basePrice: Number(q[1].adjusted_net_price),
      extras,
      t,
      quote: q,
    });
    return {
      concept: q[0],
      tooltipInnerComponent: () => (
        <PriceConceptsBreakdown concepts={dayConcepts} />
      ),
      conceptQuantity: Number(q[1].net_full_price),
    };
  });
};

const getAllDaysQuotes = ({ quote, t }) => {
  const groupedQuotes = getGroupedQuotes({ quote, t });

  return groupedQuotes.map((q) => {
    const title = t('Quote:price_for', {
      price_concept: joinElementsWithSeparatorAndEndingSeparator(
        q[0],
        ', ',
        t('Recurring:and')
      ),
    });
    return [title, { totalPriceTitle: title, ...q[1] }];
  });
};

const getPriceConceptsAndTotalPrices = ({ quote, t }) => {
  const allDaysQuotes = getAllDaysQuotes({ quote, t });

  let quoteToUse;
  let extras;
  let totalPricesQuotes;
  let basePrice;
  if (allDaysQuotes.length <= 1) {
    quoteToUse = allDaysQuotes.length === 1 ? allDaysQuotes[0][1] : quote;
    extras = getExtras({ priceInfo: quoteToUse, filterExtras: true, t });
    quoteToUse.totalPriceTitle = t('no_vat_included');
    totalPricesQuotes = [quoteToUse];
    basePrice = quoteToUse.price_breakdown?.net_price_without_discounts;
  } else {
    quoteToUse = quote;
    extras = getCommonExtras({ priceInfo: quoteToUse, filterExtras: false, t });
    totalPricesQuotes = allDaysQuotes.map((q) => q[1]);
    basePrice = Math.min(
      ...totalPricesQuotes.map((q) => Number(q.adjusted_net_price))
    );
  }

  return {
    concepts: getPriceConcepts({
      basePrice,
      extras,
      t,
      quote: quoteToUse,
    }),
    totalPrices: getTotalPrices({ quotes: totalPricesQuotes }),
  };
};

function appointmentRequired({
  stopType,
  orderType,
  poi_info,
  appointment_on_demand,
}) {
  if (appointment_on_demand) return true;
  if (!poi_info) return false;

  return (
    orderConstants.APPOINTMENTS_ENABLED.stopType[stopType] &&
    orderConstants.APPOINTMENTS_ENABLED.orderType[orderType] &&
    !!poi_info?.is_booking_required?.is_booking_required
  );
}

function getAppointmentDate({ stopCompleted, appointment, t, timezone }) {
  if (stopCompleted || !appointment || !appointment?.time_from) return null;

  return dateAndTimeFormatters.getHumanReadableFormattedDateAndTimes({
    valueFrom: appointment?.time_from,
    valueTo: appointment?.time_to,
    timezone,
    t,
  });
}

function hasAdditionalOptionsError(errors) {
  const reInternalReference = new RegExp('internal_reference');
  const reComments = new RegExp('_comment_');
  const reAppointments = new RegExp('appointment');

  return Object.keys(errors || {}).some(
    (errorKey) =>
      reInternalReference.test(errorKey) ||
      reComments.test(errorKey) ||
      reAppointments.test(errorKey)
  );
}

const isStatusBeforeAssigned = (status) =>
  CANCELLABLE_BY_SHIPPER_STATUSES.indexOf(status) >= 0;

const getIsRepeatable = (order) => !!order?.journey_type;
const getIsEditable = (order) => order?.is_editable;
const getIsCancellable = (order) =>
  !!order?.journey_type && isStatusBeforeAssigned(order?.status);

export default undefined;
export {
  getExtras,
  getPriceConcepts,
  getRecurringPriceConcepts,
  getPriceConceptsAndTotalPrices,
  buildExtraConcept,
  buildDiscountConcept,
  appointmentRequired,
  getAppointmentDate,
  hasAdditionalOptionsError,
  getNullableExtras,
  getDiscounts,
  isStatusBeforeAssigned,
  getIsCancellable,
  getIsEditable,
  getIsRepeatable,
};
